/**
 * Procedural city layout (DESIGN.md §6, Phase 2). Pure data — no Three.js, no
 * physics. Both the renderer and the collider consume this same array, so what
 * you see and what you hit can never drift apart.
 *
 * Deterministic from a seed: a crash report or a replay is meaningless if the
 * city regenerates differently next run.
 */

export interface Building {
  /** Centre of the footprint, world XZ. */
  x: number
  z: number
  /** Full width and depth, metres. */
  width: number
  depth: number
  height: number
  /** 0..1, drives facade variation and how many windows are lit. */
  variant: number
}

export interface Pad {
  x: number
  y: number
  z: number
  radius: number
  kind: 'launch' | 'landing'
}

export interface City {
  buildings: Building[]
  launch: Pad
  landing: Pad
  /** Half-extent of the built area, metres. */
  extent: number
  blockSize: number
  streetWidth: number
}

/** mulberry32 — small, fast, and good enough for city blocks. */
function rng(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export interface CityOptions {
  seed?: number
  /** Blocks per side. */
  blocks?: number
  blockSize?: number
  streetWidth?: number
}

export function generateCity(options: CityOptions = {}): City {
  const seed = options.seed ?? 1337
  const blocks = options.blocks ?? 9
  const blockSize = options.blockSize ?? 58
  const streetWidth = options.streetWidth ?? 18
  const random = rng(seed)

  const pitch = blockSize + streetWidth
  const half = Math.floor(blocks / 2)
  const buildings: Building[] = []

  for (let bx = -half; bx <= half; bx++) {
    for (let bz = -half; bz <= half; bz++) {
      const cx = bx * pitch
      const cz = bz * pitch

      // Height falls off from the centre, so the skyline has a downtown and a
      // horizon rather than reading as a uniform field of blocks.
      const distance = Math.hypot(bx, bz) / (half + 0.5)
      const coreBias = Math.max(0, 1 - distance * distance)

      // Split each block into 1, 2 or 4 lots.
      const split = random()
      const lots =
        split < 0.35
          ? [[0, 0, 1, 1]]
          : split < 0.7
            ? random() < 0.5
              ? [
                  [0, -0.25, 1, 0.5],
                  [0, 0.25, 1, 0.5],
                ]
              : [
                  [-0.25, 0, 0.5, 1],
                  [0.25, 0, 0.5, 1],
                ]
            : [
                [-0.25, -0.25, 0.5, 0.5],
                [0.25, -0.25, 0.5, 0.5],
                [-0.25, 0.25, 0.5, 0.5],
                [0.25, 0.25, 0.5, 0.5],
              ]

      for (const [ox, oz, sw, sd] of lots) {
        // A little inset so neighbouring towers do not fuse into one slab.
        const inset = 1.5 + random() * 2.5
        const width = Math.max(6, blockSize * sw - inset * 2)
        const depth = Math.max(6, blockSize * sd - inset * 2)

        const base = 12 + random() * 26
        const tower = coreBias * (30 + random() * 95)
        const height = Math.round(base + tower)

        buildings.push({
          x: cx + ox * blockSize,
          z: cz + oz * blockSize,
          width,
          depth,
          height,
          variant: random(),
        })
      }
    }
  }

  // Pads go on real rooftops: pick a tall central building to launch from and
  // a distinct one a good flight away to land on.
  const launchHost = pickPadHost(buildings, 0, 0, 55, 110)
  const landing = pickLandingHost(buildings, launchHost, 90, 160)

  return {
    buildings,
    launch: {
      x: launchHost.x,
      y: launchHost.height,
      z: launchHost.z,
      radius: Math.min(6, Math.min(launchHost.width, launchHost.depth) / 2 - 1),
      kind: 'launch',
    },
    landing: {
      x: landing.x,
      y: landing.height,
      z: landing.z,
      radius: Math.min(6, Math.min(landing.width, landing.depth) / 2 - 1),
      kind: 'landing',
    },
    extent: (half + 0.5) * pitch,
    blockSize,
    streetWidth,
  }
}

/** The roomiest rooftop near a point, within a height band. */
function pickPadHost(
  buildings: Building[],
  x: number,
  z: number,
  minHeight: number,
  maxHeight: number,
): Building {
  let best = buildings[0]
  let bestScore = -Infinity
  for (const b of buildings) {
    const roof = Math.min(b.width, b.depth)
    if (roof < 14) continue
    const distance = Math.hypot(b.x - x, b.z - z)
    const heightFit = b.height >= minHeight && b.height <= maxHeight ? 25 : 0
    // Distance dominates: a slightly smaller roof downtown beats a huge one on
    // the outskirts, or the player starts the game staring at empty suburbs.
    const score = roof + heightFit - distance * 0.6
    if (score > bestScore) {
      bestScore = score
      best = b
    }
  }
  return best
}

/** A roomy rooftop at a flyable distance from the launch pad. */
function pickLandingHost(buildings: Building[], from: Building, min: number, max: number): Building {
  let best = from
  let bestScore = -Infinity
  for (const b of buildings) {
    if (b === from) continue
    const roof = Math.min(b.width, b.depth)
    if (roof < 14) continue
    const distance = Math.hypot(b.x - from.x, b.z - from.z)
    if (distance < min || distance > max) continue
    // Prefer a big roof that is lower than the launch pad — gliding down to a
    // target reads better as a first challenge than climbing to one.
    const score = roof + (from.height - b.height) * 0.25
    if (score > bestScore) {
      bestScore = score
      best = b
    }
  }
  return best
}
